import React, { Component } from 'react';
import Form from 'react-bootstrap/Form';

import Select from 'components/controls/select';
import CellWorkflowView, { CELL_WORKFLOW_MODE_LABELS } from './cellWorkflowView';
import WorkflowGraph from './workflowGraph';
import AgentPopover, { chipAnchorRect } from '../../components/agentPopover';
import { isStructureEditable } from './cellRules';
import {
  workflowBody, workflowAlias, workflowSelection, agentSelection, resolvableAgents, withStepAgent,
  agentLabel, isSameAgent,
} from '../../domain/cellWorkflow';
import { upsertCell } from '../../domain/model';
import { CELL_WORKFLOW_MODES } from '../../domain/schema';

/**
 * CellWorkflowSection — which workflow a cell runs, choosable or frozen, and the rule for which.
 *
 * Extracted for the reason CellDetailsSection was, and the rule is the same one: a cell is editable
 * while it is a draft AND the revision and session allow it. It lived in the dock panel, so the
 * cell's own screen — one click away, reading as the same product — could only ever show what had
 * been chosen.
 *
 * The mode and the workflow travel TOGETHER on every write. A mode change without its resolved
 * workflow, or the reverse, leaves the two inconsistent server-side, and only one of them decides
 * what actually runs.
 *
 * `specify_workflow` is the only mode that keeps a workflow; both inherit modes clear it, because
 * they are resolved at dispatch from the mission or the previous cell. What is written is the
 * BODY, resolved from the palette at pick time — a bare name would reintroduce the reference
 * semantics the embedded copy exists to avoid, and the palette moves on while the cell does not.
 *
 * **Which agent a step runs** is picked the same way: tapping a `worker_task` step's chip in the
 * preview selects that step (tracked here, in `state.pickedStep` — NOT ReactFlow node selection,
 * which still means "this is where the run is") — including a step with no agent yet, so an agent
 * can be SET, not just changed. An `AgentPopover` (components/agentPopover.js — shared with
 * MissionPanel) opens anchored to the tapped chip, over `templates.agents`, and choosing one writes
 * the palette entry's five-field block onto that step via `withStepAgent`, through this section's
 * one write path (`_save`). Picking only ever REPLACES a step's agent — there is no "no agent"
 * choice, because `cell.workflow_overrides.agent` (the only other source dispatch would fall back
 * to) has no authoring surface anywhere in the product, so a cleared `worker_task` step would just
 * fail with `agent_unresolved`. The gate is the section's own — editable in draft only, same as the
 * workflow and workspace pickers.
 *
 * The popover's other half BUILDS a block instead of picking one, off `templates.harnesses`, and
 * lands in `_buildAgent` — the same write path with a different source for the block.
 *
 * Props:
 *   cell, missionId, client   — what to write and where
 *   templates                 — store.templates; `workflows` is the palette to choose from,
 *                                `agents` is the palette a step's agent is picked from
 *   onApplyLocal              — (updater) => void; reflect the write without a reload
 *   canEdit                   — false on a committed revision, a closed session, or no write access
 */
const NONE_OPTION = { value: '', label: '— none —' };

class CellWorkflowSection extends Component {
  state = { pickedStep: null, anchorRect: null };

  // Holds the graph preview's DOM node so the popover can be positioned from the tapped chip's own
  // getBoundingClientRect() — see _openAgentPopover.
  _graphRef = React.createRef();

  _workflows() {
    return (this.props.templates && this.props.templates.workflows) || [];
  }

  // Opens the popover anchored to the step's own chip. Looked up by DOM query rather than threaded
  // through as a second onSelectStep argument, so WorkflowGraph's own (stepId) => void contract —
  // and every test built against it — stays exactly as it was. Resolving the actual rect is
  // chipAnchorRect's job — see its docstring for why the button itself cannot be measured directly.
  _openAgentPopover(stepId) {
    this.setState({ pickedStep: stepId, anchorRect: chipAnchorRect(this._graphRef.current, stepId) });
  }

  _clearPickedStep() {
    this.setState({ pickedStep: null, anchorRect: null });
  }

  // The one write path: the pair, then the local reflection.
  async _save(mode, cellWorkflow) {
    const { cell, missionId, client, onApplyLocal } = this.props;
    if (!cell || !client) return;
    const changed = { cell_workflow_mode: mode, cell_workflow: cellWorkflow };
    try {
      await client.setCellProps(missionId, cell.element_id, changed);
      if (onApplyLocal) {
        onApplyLocal(s => upsertCell(s, missionId, {
          ...cell, props: { ...(cell.props || {}), ...changed },
        }));
      }
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveMode(mode) {
    const { cell } = this.props;
    const next = mode || '';
    if (next === ((cell.props || {}).cell_workflow_mode || '')) return;
    const keep = next === 'specify_workflow' ? ((cell.props || {}).cell_workflow || null) : null;
    // A mode change is a new answer to "what is this cell running", which makes any step picked
    // under the old one stale — same reason _saveWorkflow below clears it.
    this._clearPickedStep();
    await this._save(next || null, keep);
  }

  async _saveWorkflow(value) {
    const { cell } = this.props;
    const selected = value || '';
    if (selected === workflowAlias(cell && cell.props && cell.props.cell_workflow)) return;
    // A different workflow is a different step graph; a step id picked in the old one would not
    // resolve to anything real in the new one.
    this._clearPickedStep();
    await this._save('specify_workflow', workflowSelection(selected, this._workflows()));
  }

  // AgentPopover's onPick for the picked step: writes the palette entry's five-field block onto
  // that one step and saves through the one write path.
  //
  // Two guards, in order: re-picking the step's own current agent is a no-op (mirrors every sibling
  // save in this file), and an alias agentSelection cannot resolve (a malformed or stale palette
  // entry) refuses the write rather than falling through into anything else.
  async _saveAgent(alias) {
    const { cell, templates } = this.props;
    const { pickedStep } = this.state;
    if (!pickedStep) return;
    const body = workflowBody((cell.props || {}).cell_workflow, this._workflows());
    if (!body) return;
    const pickedNode = Array.isArray(body.nodes) ? body.nodes.find(n => n && n.id === pickedStep) : null;
    const current = (pickedNode && agentLabel(pickedNode)) || '';
    if (alias === current) return;
    const agents = (templates && templates.agents) || [];
    const agent = agentSelection(alias, agents);
    if (!agent) return;
    const nextBody = withStepAgent(body, pickedStep, agent);
    await this._save('specify_workflow', nextBody);
  }

  // AgentPopover's onBuild for the picked step: the block came off the four sliders rather than the
  // palette, and everything after that is _saveAgent's path exactly.
  //
  // Two guards, differing from _saveAgent's for the same two reasons: there is no alias to compare
  // against, so the no-op guard compares the WHOLE block (a release that changed nothing must not
  // post a save); and there is no null resolution to absorb, because customAgentBlock already
  // returned either null or a complete block — so the guard is simply that a block arrived.
  async _buildAgent(block) {
    const { cell } = this.props;
    const { pickedStep } = this.state;
    if (!pickedStep || !block) return;
    const body = workflowBody((cell.props || {}).cell_workflow, this._workflows());
    if (!body) return;
    const pickedNode = Array.isArray(body.nodes) ? body.nodes.find(n => n && n.id === pickedStep) : null;
    if (pickedNode && isSameAgent(pickedNode.agent, block)) return;
    const nextBody = withStepAgent(body, pickedStep, block);
    await this._save('specify_workflow', nextBody);
  }

  render() {
    const { cell, canEdit } = this.props;
    const workflows = this._workflows();
    if (!isStructureEditable(cell) || canEdit === false) {
      // The frozen view: once a cell has started there is no choice left to describe, so the mode,
      // the help text and the pickers all go and what it IS running is the whole of it.
      return <CellWorkflowView cell={cell} workflows={workflows} />;
    }

    const props = cell.props || {};
    // Never the body itself: passing it to JSX is what took the whole widget down once with
    // "Objects are not valid as a React child" (see domain/cellWorkflow.js).
    const key = workflowAlias(props.cell_workflow);
    // Older cells predate cell_workflow_mode: a bare workflow reads as specify_workflow.
    const mode = props.cell_workflow_mode || (key ? 'specify_workflow' : null);

    const modeOptions = CELL_WORKFLOW_MODES.map(
      value => ({ value, label: CELL_WORKFLOW_MODE_LABELS[value] || value }));
    const workflowOptions = [
      NONE_OPTION,
      ...workflows.map(entry => entry.alias).filter(Boolean)
        .map(alias => ({ value: alias, label: alias })),
    ];
    // The step graph — resolved once and reused by both the preview and the agent picker below it.
    const body = workflowBody(props.cell_workflow, workflows);

    const { pickedStep, anchorRect } = this.state;
    const pickedNode = (pickedStep && body && Array.isArray(body.nodes))
      ? body.nodes.find(n => n && n.id === pickedStep) : null;
    // Only entries agentSelection can actually resolve — never offer a choice that would silently
    // write nothing (see _saveAgent's guard, and resolvableAgents in domain/cellWorkflow.js).
    const pickableAgents = resolvableAgents((this.props.templates && this.props.templates.agents) || []);
    // '' for no agent at all, 'custom' for an alias-less block (still HAS an agent — see
    // agentLabel's docstring), or the block's own alias. The popover checks a row only when this
    // matches ITS alias, so 'custom' — not one of the listed choices — correctly checks nothing.
    const pickedAgentAlias = (pickedNode && agentLabel(pickedNode)) || '';

    return (
      <>
        <Form.Group>
          <Select
            options={modeOptions}
            value={modeOptions.find(option => option.value === mode) || null}
            onChange={option => this._saveMode(option ? option.value : '')}
            menuPortalTarget={document.body}
            placeholder="Choose a mode…"
          />
          <Form.Text muted>How the workflow is chosen.</Form.Text>
        </Form.Group>
        {mode === 'specify_workflow' && (
          <Form.Group>
            <Select
              options={workflowOptions}
              value={workflowOptions.find(option => option.value === key) || NONE_OPTION}
              onChange={option => this._saveWorkflow(option ? option.value : '')}
              menuPortalTarget={document.body}
              placeholder="Choose a workflow…"
            />
            {/* The chosen workflow's step graph, right under the picker. Tapping a step's chip opens
                the agent popover anchored to it — see _openAgentPopover. */}
            {body && (
              <div ref={this._graphRef}>
                <WorkflowGraph workflow={body} onSelectStep={stepId => this._openAgentPopover(stepId)} />
              </div>
            )}
            <Form.Text muted>The steps this cell runs.</Form.Text>
          </Form.Group>
        )}
        {mode === 'specify_workflow' && pickedStep && pickedNode && (
          <AgentPopover
            anchorRect={anchorRect}
            stepId={pickedStep}
            agents={pickableAgents}
            selectedAlias={pickedAgentAlias}
            // _saveAgent reads state.pickedStep synchronously before it awaits anything, so it must
            // run BEFORE _clearPickedStep's setState — reversing the order would clear the very step
            // id the write needs. A picked row writes, then closes.
            onPick={alias => { this._saveAgent(alias); this._clearPickedStep(); }}
            harnesses={(this.props.templates && this.props.templates.harnesses) || []}
            currentAgent={pickedNode && pickedNode.agent}
            // Deliberately NOT paired with _clearPickedStep, unlike onPick above. A pick is a
            // decision and closing on it is the answer; a slider is a tuning gesture, and closing
            // would mean re-opening the popover to make the next adjustment.
            onBuild={block => this._buildAgent(block)}
            onClose={() => this._clearPickedStep()}
          />
        )}
      </>
    );
  }
}

export default CellWorkflowSection;
